import { useState, useEffect, useRef } from "react";
import { MessageCircle, Send, X, Loader2, User, Car } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { API_URL } from "@/config/api";

// In-App Chat Component for Client <-> Driver communication
export function ChatWidget({ bookingId, userType = "client", userName, driverName, autoOpenOnMessage = false, onOpenChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showLabel, setShowLabel] = useState(true);
  const [lastMessageCount, setLastMessageCount] = useState(0);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const labelIntervalRef = useRef(null);

  // Notify parent when isOpen changes
  const handleSetIsOpen = (value) => {
    setIsOpen(value);
    if (onOpenChange) {
      onOpenChange(value);
    }
  };

  // Toggle label visibility periodically
  useEffect(() => {
    const showLabelDuration = 5000;
    const hideLabelDuration = 10000;
    
    const toggleLabel = () => {
      setShowLabel(true);
      setTimeout(() => setShowLabel(false), showLabelDuration);
    };

    // Initial hide after 5 seconds
    setTimeout(() => setShowLabel(false), showLabelDuration);
    
    // Periodic toggle
    labelIntervalRef.current = setInterval(toggleLabel, hideLabelDuration + showLabelDuration);
    
    return () => {
      if (labelIntervalRef.current) clearInterval(labelIntervalRef.current);
    };
  }, []);

  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Fetch messages
  const fetchMessages = async () => {
    if (!bookingId) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/messages/${bookingId}`);
      const newMessages = response.data.messages || [];
      
      // Count unread messages from the other party
      const unread = newMessages.filter(m => 
        m.sender_type !== userType && !m.read
      ).length;
      setUnreadCount(unread);
      
      // Don't auto-open chat - the notification badge is enough
      // if (autoOpenOnMessage && !isOpen && newMessages.length > lastMessageCount) {
      //   const latestMessage = newMessages[newMessages.length - 1];
      //   if (latestMessage && latestMessage.sender_type !== userType) {
      //     handleSetIsOpen(true);
      //   }
      // }
      
      setLastMessageCount(newMessages.length);
      setMessages(newMessages);
      
      // Mark as read if chat is open
      if (isOpen && unread > 0) {
        await axios.put(`${API_URL}/api/messages/${bookingId}/read`, {
          reader_type: userType
        });
        setUnreadCount(0);
      }
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    }
  };

  // Initial fetch and polling
  useEffect(() => {
    if (bookingId) {
      fetchMessages();
      
      // Poll for new messages every 10 seconds
      pollIntervalRef.current = setInterval(fetchMessages, 10000);
      
      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    }
  }, [bookingId]);

  // Scroll to bottom when messages change or chat opens
  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
      // Mark messages as read when opening
      if (unreadCount > 0) {
        axios.put(`${API_URL}/api/messages/${bookingId}/read`, {
          reader_type: userType
        }).then(() => setUnreadCount(0));
      }
    }
  }, [isOpen, messages]);

  // Send message
  const handleSend = async () => {
    if (!newMessage.trim() || !bookingId) return;
    
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/messages/${bookingId}`, {
        content: newMessage.trim(),
        sender_type: userType,
        sender_name: userName || (userType === "client" ? "Client" : "Driver")
      });
      
      setNewMessage("");
      await fetchMessages();
    } catch (error) {
      console.error("Failed to send message:", error);
    } finally {
      setSending(false);
    }
  };

  // Handle Enter key
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = formatDate(message.timestamp);
    if (!groups[date]) groups[date] = [];
    groups[date].push(message);
    return groups;
  }, {});

  if (!bookingId) return null;

  return (
    <>
      {/* Chat Button - Blue bubble for driver chat */}
      <button
        onClick={() => handleSetIsOpen(true)}
        className="fixed bottom-20 right-4 md:bottom-6 md:right-6 z-40 group"
        data-testid="chat-button"
      >
        <div className="relative flex items-center">
          {/* Label that appears periodically */}
          <div 
            className={`absolute right-full mr-3 px-3 py-2 bg-slate-900 text-white text-sm rounded-lg whitespace-nowrap transition-all duration-300 ${
              showLabel ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2 pointer-events-none'
            }`}
          >
            <span className="font-medium">
              {userType === "client" ? "Chat with your driver" : "Chat with your client"}
            </span>
            <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1 w-2 h-2 bg-slate-900 rotate-45"></div>
          </div>
          
          {/* Blue Button with Car/User icon */}
          <div className="relative w-14 h-14 bg-blue-500 hover:bg-blue-600 rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110">
            {userType === "client" ? (
              <Car className="w-7 h-7 text-white" />
            ) : (
              <User className="w-7 h-7 text-white" />
            )}
            
            {/* Unread Badge - Red circle with count */}
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center border-2 border-white animate-bounce">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
            
            {/* Pulse animation ring - like an echo effect */}
            <span className="absolute inset-0 rounded-full border-2 border-blue-400 animate-ping opacity-40"></span>
            <span className="absolute inset-0 rounded-full border border-blue-300 animate-pulse opacity-60"></span>
          </div>
        </div>
      </button>

      {/* Chat Modal */}
      {isOpen && (
        <div className="fixed inset-0 md:inset-auto md:bottom-6 md:right-6 md:w-96 md:h-[500px] bg-white md:rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center overflow-hidden">
                {userType === "client" ? (
                  <Car className="w-5 h-5" />
                ) : (
                  <User className="w-5 h-5" />
                )}
              </div>
              <div>
                <h3 className="font-semibold">
                  {userType === "client" ? (driverName || "Your Driver") : (userName || "Client")}
                </h3>
                <p className="text-xs text-blue-100">Booking chat • Online</p>
              </div>
            </div>
            <button
              onClick={() => handleSetIsOpen(false)}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 bg-slate-50">
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-slate-400">
                <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mb-3">
                  {userType === "client" ? (
                    <Car className="w-8 h-8 text-blue-500" />
                  ) : (
                    <User className="w-8 h-8 text-blue-500" />
                  )}
                </div>
                <p className="text-sm font-medium text-slate-600">
                  {userType === "client" ? "Chat with your driver" : "Chat with your client"}
                </p>
                <p className="text-xs mt-1 text-slate-400">Start the conversation!</p>
              </div>
            ) : (
              Object.entries(groupedMessages).map(([date, dateMessages]) => (
                <div key={date}>
                  {/* Date separator */}
                  <div className="flex items-center justify-center my-4">
                    <span className="px-3 py-1 bg-slate-200 text-slate-600 text-xs rounded-full">
                      {date}
                    </span>
                  </div>
                  
                  {/* Messages */}
                  {dateMessages.map((message, index) => {
                    const isMe = message.sender_type === userType;
                    return (
                      <div
                        key={message.id || index}
                        className={`flex mb-3 ${isMe ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] px-4 py-2 rounded-2xl ${
                            isMe
                              ? 'bg-blue-500 text-white rounded-br-md'
                              : 'bg-white text-slate-800 rounded-bl-md shadow-sm'
                          }`}
                        >
                          {!isMe && (
                            <p className="text-xs font-medium text-blue-500 mb-1">
                              {message.sender_name}
                            </p>
                          )}
                          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                          <p className={`text-xs mt-1 ${isMe ? 'text-blue-100' : 'text-slate-400'}`}>
                            {formatTime(message.timestamp)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 bg-white border-t border-slate-200 shrink-0">
            <div className="flex gap-2">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type a message..."
                className="flex-1 px-4 py-2 bg-slate-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={sending}
                data-testid="chat-input"
              />
              <Button
                onClick={handleSend}
                disabled={!newMessage.trim() || sending}
                className="w-10 h-10 p-0 bg-blue-500 hover:bg-blue-600 rounded-full"
                data-testid="chat-send-btn"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ChatWidget;
